import { Rectangle, Texture } from 'pixi.js';
import rocketUrl from '../assets/rocket.png';
import ripUrl from '../assets/rip.png';
import wRifleUrl from '../assets/w_rifle.png';
import wLaserUrl from '../assets/w_laser.png';
import wRocketUrl from '../assets/w_rocket.png';
import explosionUrl from '../assets/explosion.png';
import scorchUrl from '../assets/scorch.png';

/**
 * Процедурные текстуры. Генерируются один раз при старте — никаких сетевых
 * ассетов: браузер-сорс в OBS должен подниматься с пустого кэша мгновенно.
 */

function canvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  return [c, c.getContext('2d')!];
}

function makeDisc(size = 128): Texture {
  const [c, ctx] = canvas(size);
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
  ctx.fill();
  return Texture.from(c);
}

function makeRing(size = 128, thickness = 9): Texture {
  const [c, ctx] = canvas(size);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = thickness;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - thickness / 2 - 1, 0, Math.PI * 2);
  ctx.stroke();
  return Texture.from(c);
}

/**
 * Ободок-индикатор HP: набор дуг от пустой до полной.
 *
 * Рисовать 500 дуг через Graphics каждый кадр нельзя, поэтому HP округляется
 * до одной из ступеней и берётся готовая текстура. Ступеней хватает, чтобы
 * глаз не замечал дискретности.
 */
export const HP_STEPS = 24;

function makeHpArcs(size = 128, thickness = 11): Texture[] {
  const out: Texture[] = [];
  for (let i = 0; i <= HP_STEPS; i++) {
    const [c, ctx] = canvas(size);
    const frac = i / HP_STEPS;
    if (frac > 0) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = thickness;
      ctx.lineCap = 'round';
      ctx.beginPath();
      // Полоса растёт от верхней точки по часовой стрелке.
      ctx.arc(
        size / 2,
        size / 2,
        size / 2 - thickness / 2 - 1,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * frac,
      );
      ctx.stroke();
    }
    out.push(Texture.from(c));
  }
  return out;
}

function makeGlow(size = 128): Texture {
  const [c, ctx] = canvas(size);
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(c);
}

function makePixel(): Texture {
  const [c, ctx] = canvas(1);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, 1, 1);
  return Texture.from(c);
}

function makeEmoji(ch: string, size = 64): Texture {
  const [c, ctx] = canvas(size);
  ctx.font = `${Math.floor(size * 0.8)}px system-ui, "Noto Color Emoji", "Segoe UI Emoji", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(ch, size / 2, size / 2 + 2);
  return Texture.from(c);
}

/**
 * Цифры уровня. Их ровно десять на всю игру — одна текстура на уровень,
 * общая для всех бойцов, сколько бы их ни было на поле.
 */
function makeLevelBadges(maxLevel: number, size = 64): Texture[] {
  const out: Texture[] = [];
  for (let lv = 1; lv <= maxLevel; lv++) {
    const [c, ctx] = canvas(size);
    ctx.font = `900 ${Math.floor(size * 0.62)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = size * 0.18;
    ctx.strokeStyle = 'rgba(3,6,12,0.95)';
    ctx.strokeText(String(lv), size / 2, size / 2);
    ctx.fillStyle = '#fff';
    ctx.fillText(String(lv), size / 2, size / 2);
    out.push(Texture.from(c));
  }
  return out;
}

/**
 * Ракета — единственная растровая текстура в игре. Картинка нарисована носом
 * вверх, поэтому спрайту при полёте добавляется +90°.
 */
function makeRocket(): Texture {
  const img = new Image();
  const tex = Texture.from(img);
  img.onload = () => tex.source.update();
  img.src = rocketUrl;
  return tex;
}

/**
 * Надгробие на месте гибели.
 *
 * Белый фон выбивается по порогу: исходник нарисован на белом, и без этого
 * на поле появлялся бы белый квадрат вместо камня. Порог высокий (246),
 * поэтому светло-бежевый камень и голубой крест остаются нетронутыми.
 */
function makeRip(): Texture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  const tex = Texture.from(c);

  const img = new Image();
  img.onload = () => {
    ctx.clearRect(0, 0, 256, 256);
    ctx.drawImage(img, 0, 0, 256, 256);
    const data = ctx.getImageData(0, 0, 256, 256);
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      if (px[i]! >= 246 && px[i + 1]! >= 246 && px[i + 2]! >= 246) px[i + 3] = 0;
    }
    ctx.putImageData(data, 0, 0);
    tex.source.update();
  };
  img.src = ripUrl;
  return tex;
}

/**
 * След от взрыва — воронка, остающаяся на земле.
 *
 * У исходника тёмная виньетка по краям: если наложить его как есть, на поле
 * появится квадрат с чёрной каймой. Поэтому альфа гасится радиально — центр
 * непрозрачный, к краю сходит в ноль, и след ложится на арену пятном без
 * видимых границ.
 */
function makeScorch(size = 512): Texture {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  const tex = Texture.from(c);

  const img = new Image();
  img.onload = () => {
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(img, 0, 0, size, size);

    const data = ctx.getImageData(0, 0, size, size);
    const px = data.data;
    const half = size / 2;
    // Полностью непрозрачно до 0.40 радиуса, дальше плавно в ноль к 0.48 —
    // углы исходника с виньеткой отсекаются целиком.
    const inner = half * 0.8;
    const outer = half * 0.96;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const d = Math.hypot(x - half, y - half);
        const i = (y * size + x) * 4 + 3;
        if (d <= inner) continue;
        px[i] = d >= outer ? 0 : Math.round(px[i]! * (1 - (d - inner) / (outer - inner)));
      }
    }
    ctx.putImageData(data, 0, 0);
    tex.source.update();
  };
  img.src = scorchUrl;
  return tex;
}

/** Раскладка атласа взрыва: 40 кадров исходной гифки, сетка 8×5. */
export const EXPLOSION_COLS = 8;
export const EXPLOSION_ROWS = 5;
export const EXPLOSION_FRAMES = 40;
const EXPLOSION_FW = 160;
const EXPLOSION_FH = 120;

/**
 * Кадры взрыва из спрайт-атласа.
 *
 * Гифку разобрали в атлас заранее, а не декодируем в браузере: декодера для
 * GIF в Pixi нет, а тянуть зависимость ради одного эффекта незачем. Все сорок
 * кадров делят одну текстуру, меняется только прямоугольник выборки.
 *
 * Фон в исходнике чёрный, поэтому спрайт рисуется аддитивно — чёрное
 * становится прозрачным само, а огонь начинает светиться.
 */
function makeExplosionFrames(): Texture[] {
  const img = new Image();
  const base = Texture.from(img);
  img.onload = () => base.source.update();
  img.src = explosionUrl;

  const out: Texture[] = [];
  for (let i = 0; i < EXPLOSION_FRAMES; i++) {
    const x = (i % EXPLOSION_COLS) * EXPLOSION_FW;
    const y = Math.floor(i / EXPLOSION_COLS) * EXPLOSION_FH;
    out.push(
      new Texture({
        source: base.source,
        frame: new Rectangle(x, y, EXPLOSION_FW, EXPLOSION_FH),
      }),
    );
  }
  return out;
}

/**
 * Вертикальный градиент «прозрачно → плотно → прозрачно».
 *
 * Растягивается на всю ширину и служит тенью нейтральной полосы: жёсткий
 * прямоугольник выглядит нарисованным, а мягкая тень читается как провал
 * между двумя армиями.
 */
function makeSeam(h = 128): Texture {
  const c = document.createElement('canvas');
  c.width = 4;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.5, 'rgba(255,255,255,1)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 4, h);
  return Texture.from(c);
}

/**
 * Плитка оружия для плашки цен.
 *
 * Все картинки проходят одну и ту же обработку: обрезка по центру в квадрат
 * и скругление углов. Исходники разные — одна на сером фоне, две в рамках, —
 * и без общего приведения колонка выглядит набором случайных файлов. Единый
 * квадрат делает из них один комплект.
 */
function makeWeaponTile(url: string, size = 192): Texture {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  const tex = Texture.from(c);

  const img = new Image();
  img.onload = () => {
    ctx.clearRect(0, 0, size, size);
    ctx.save();
    const r = size * 0.16;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(size, 0, size, size, r);
    ctx.arcTo(size, size, 0, size, r);
    ctx.arcTo(0, size, 0, 0, r);
    ctx.arcTo(0, 0, size, 0, r);
    ctx.closePath();
    ctx.clip();
    const side = Math.min(img.width, img.height);
    ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
    ctx.restore();
    tex.source.update();
  };
  img.src = url;
  return tex;
}

export interface Tex {
  disc: Texture;
  ring: Texture;
  ringThin: Texture;
  hpArcs: Texture[];
  glow: Texture;
  px: Texture;
  crown: Texture;
  rocket: Texture;
  rip: Texture;
  wRifle: Texture;
  wLaser: Texture;
  wRocket: Texture;
  explosion: Texture[];
  scorch: Texture;
  seam: Texture;
  levels: Texture[];
}

let cached: Tex | null = null;

export function getTextures(maxLevel = 10): Tex {
  if (cached) return cached;
  cached = {
    disc: makeDisc(128),
    ring: makeRing(128, 10),
    ringThin: makeRing(128, 4),
    hpArcs: makeHpArcs(128, 11),
    glow: makeGlow(128),
    px: makePixel(),
    crown: makeEmoji('👑', 64),
    rocket: makeRocket(),
    rip: makeRip(),
    wRifle: makeWeaponTile(wRifleUrl),
    wLaser: makeWeaponTile(wLaserUrl),
    wRocket: makeWeaponTile(wRocketUrl),
    explosion: makeExplosionFrames(),
    scorch: makeScorch(),
    seam: makeSeam(),
    levels: makeLevelBadges(maxLevel, 64),
  };
  return cached;
}
